import React, { useContext, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { ForecastContext } from '../../../objects/Context';
import { generatePeriods } from '../../../components/Forecast/utils/calendarUtils';
import { formatPeriodLabel } from '../../../components/Forecast/utils/formatters';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-icon-noFill.svg';
import './Overview.css';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// ── Formatting helpers ─────────────────────────────────────────────────────────
const fmt = (v) => {
  if (v === null || v === undefined || v === 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
};

const fmtCompact = (v) => {
  if (v === null || v === undefined) return '—';
  if (v === 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
    notation: 'compact',
  }).format(v);
};

// Compact formatter with one decimal (e.g. $1.2M)
const fmtCompact1 = (v) => {
  if (v === null || v === undefined) return '—';
  if (v === 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 1, maximumFractionDigits: 1,
    notation: 'compact',
  }).format(v);
};

const fmtPct = (v) => {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(1)}%`;
};

// ── Data helpers ──────────────────────────────────────────────────────────────
const collectRows = (node, rows = []) => {
  if (!node) return rows;
  if (node.kind === 'row' && !node._isDriverDisplay && !node._isRefChild) {
    rows.push(node);
  }
  (node.children || []).forEach(c => collectRows(c, rows));
  return rows;
};

const sumP = (values, periods) => {
  if (!values) return 0;
  return periods.reduce((s, p) => s + (values[p] || 0), 0);
};

const findNode = (children, ...ids) => {
  for (const id of ids) {
    const n = children.find(c => c.id === id);
    if (n) return n;
  }
  for (const id of ids) {
    const lbl = id.replace(/_/g, ' ');
    const n = children.find(c => c.label?.toLowerCase().includes(lbl));
    if (n) return n;
  }
  return null;
};

// ── Summary-row definitions ────────────────────────────────────────────────────
const SUMMARY_ROWS = [
  { id: 'beginning_cash', label: 'Beginning Cash', ids: ['beginning_cash', 'beg_cash', 'begin_cash'], isBalance: true },
  { id: 'op_inflows',     label: 'Inflows',         ids: ['op_inflows', 'total_inflows', 'inflows'],                 isBalance: false },
  { id: 'op_outflows',    label: 'Outflows',         ids: ['op_outflows', 'total_outflows', 'outflows'],              isBalance: false },
  { id: 'financing',      label: 'Financing',        ids: ['net_financing', 'financing', 'financing_activities'],     isBalance: false },
  { id: 'ending_cash',    label: 'Ending Cash',      ids: ['ending_cash', 'end_cash'],                               isBalance: true, emphasized: true },
];

const WINDOW = 10;

// ── Color palette (shared across all charts) ─────────────────────────────────
const CHART_COLORS = [
  'rgba(59,  130, 246, 0.88)',   // blue
  'rgba(239,  68,  68, 0.88)',   // red
  'rgba(16,  185,  85, 0.88)',   // green
  'rgba(245, 158,  11, 0.88)',   // amber
  'rgba(139,  92, 246, 0.88)',   // violet
  'rgba(6,   182, 212, 0.85)',   // cyan
  'rgba(236,  72, 153, 0.85)',   // pink
  'rgba(34,  197,  94, 0.82)',   // emerald
  'rgba(99,   89, 199, 0.88)',   // indigo
  'rgba(234, 179,   8, 0.82)',   // yellow
];

const CHART_COLORS_FADED = CHART_COLORS.map(c =>
  c.replace(/[\d.]+\)$/, '0.5)')
);

// ── Static chart options ─────────────────────────────────────────────────────
const REV_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    datalabels: { display: false },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: (ctx) => ` ${ctx.dataset.label}: ${fmtCompact(ctx.parsed.y)}`,
        footer: (items) => {
          const total = items.reduce((sum, item) => sum + (item.parsed?.y || 0), 0);
          return `Total: ${fmtCompact(total)}`;
        },
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: { font: { family: 'Almarai-Regular', size: 10 }, color: '#73818f', maxRotation: 45 },
    },
    y: {
      stacked: true,
      grid: { color: 'rgba(0,0,0,0.06)', drawBorder: false },
      ticks: {
        callback: (v) => fmtCompact(v),
        font: { family: 'Almarai-Regular', size: 10 },
        color: '#73818f',
      },
    },
  },
};

const getChartPeriods = (actualPeriods, forecastPeriods) => {
  const aSlice = actualPeriods.slice(-10);
  const fSlice = forecastPeriods.slice(0, 13);
  return { aSlice, chartPeriods: [...aSlice, ...fSlice], numActual: aSlice.length };
};

const toMonthlyRate = (value, granularity) => {
  if (value == null) return null;
  if (granularity === 'week') return value * (52 / 12);
  if (granularity === 'day') return value * (365 / 12);
  return value;
};

const periodsToMonths = (periods, granularity) => {
  if (periods == null) return null;
  if (granularity === 'week') return periods / (52 / 12);
  if (granularity === 'day') return periods / (365 / 12);
  return periods;
};

const buildStackedBarChartData = ({
  components,
  componentId,
  actualPeriods,
  forecastPeriods,
  granularity,
  colors,
  fadedColors,
  stack,
  maxSegments = 5,
  getValue = (v) => Math.max(Number(v) || 0, 0),
}) => {
  if (!components) return null;

  const { chartPeriods, numActual } = getChartPeriods(actualPeriods, forecastPeriods);
  if (!chartPeriods.length) return null;

  const component = components.find(c => c.id === componentId);
  if (!component) return null;

  const rows = [];
  (component.view?.children || []).forEach(ch => collectRows(ch, rows));

  const withTotals = rows
    .map((row) => ({
      label: row.label || row.id,
      total: chartPeriods.reduce((sum, period) => sum + getValue(row.values?.[period]), 0),
      values: row.values || {},
    }))
    .filter(row => row.total > 0)
    .sort((a, b) => b.total - a.total);

  if (!withTotals.length) return null;

  const top = withTotals.slice(0, maxSegments);
  const otherRows = withTotals.slice(maxSegments);
  const labels = chartPeriods.map(period => formatPeriodLabel(period, granularity));

  const datasets = top.map((row, index) => ({
    label: row.label,
    data: chartPeriods.map(period => getValue(row.values?.[period])),
    backgroundColor: chartPeriods.map((_, periodIndex) =>
      periodIndex < numActual ? colors[index] : fadedColors[index]
    ),
    borderWidth: 0,
    stack,
  }));

  if (otherRows.length > 0) {
    const colorIndex = Math.min(maxSegments, colors.length - 1);
    datasets.push({
      label: 'Other',
      data: chartPeriods.map(period =>
        otherRows.reduce((sum, row) => sum + getValue(row.values?.[period]), 0)
      ),
      backgroundColor: chartPeriods.map((_, periodIndex) =>
        periodIndex < numActual ? colors[colorIndex] : fadedColors[colorIndex]
      ),
      borderWidth: 0,
      stack,
    });
  }

  return {
    labels,
    datasets,
    segLabels: otherRows.length > 0 ? [...top.map(row => row.label), 'Other'] : top.map(row => row.label),
  };
};

// ── Component ─────────────────────────────────────────────────────────────────
function Overview() {
  const { compiledForecast: forecast, components } = useContext(ForecastContext);

  const periods         = useMemo(() => generatePeriods(forecast?.calendar), [forecast?.calendar]);
  const actualCount     = forecast?.calendar?.actual_period_count || 0;
  const granularity     = forecast?.calendar?.granularity || 'week';

  const actualPeriods   = useMemo(() => periods.slice(0, actualCount),  [periods, actualCount]);
  const forecastPeriods = useMemo(() => periods.slice(actualCount),     [periods, actualCount]);

  const actWin  = useMemo(() => actualPeriods.slice(-WINDOW),        [actualPeriods]);
  const fcstWin = useMemo(() => forecastPeriods.slice(0, WINDOW),    [forecastPeriods]);

  // ── Summary nodes ─────────────────────────────────────────────────────────
  const summaryNodes = useMemo(() => {
    if (!components) return {};
    const sc = components.find(c => c.id === 'summary');
    if (!sc?.view?.children) return {};
    const ch = sc.view.children;
    const out = {};
    SUMMARY_ROWS.forEach(r => { out[r.id] = findNode(ch, ...r.ids); });
    return out;
  }, [components]);

  // ── Summary table rows ────────────────────────────────────────────────────
  const tableRows = useMemo(() => {
    return SUMMARY_ROWS.map(def => {
      const node = summaryNodes[def.id];
      if (!node?.values) return { ...def, actVal: null, fcstVal: null, varD: null, varP: null };

      let actVal, fcstVal;
      if (def.isBalance) {
        if (def.id === 'beginning_cash') {
          actVal  = actWin.length  ? (node.values[actWin[0]]  ?? null) : null;
          fcstVal = fcstWin.length ? (node.values[fcstWin[0]] ?? null) : null;
        } else {
          actVal  = actWin.length  ? (node.values[actWin[actWin.length - 1]]   ?? null) : null;
          fcstVal = fcstWin.length ? (node.values[fcstWin[fcstWin.length - 1]] ?? null) : null;
        }
      } else {
        actVal  = sumP(node.values, actWin)  || null;
        fcstVal = sumP(node.values, fcstWin) || null;
      }

      const varD = (actVal !== null && fcstVal !== null) ? fcstVal - actVal : null;
      const varP = (varD !== null && actVal && actVal !== 0) ? varD / Math.abs(actVal) : null;
      return { ...def, actVal, fcstVal, varD, varP };
    });
  }, [summaryNodes, actWin, fcstWin]);

  // ── Key metrics ────────────────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const ecNode = summaryNodes['ending_cash'];
    const bcNode = summaryNodes['beginning_cash'];
    const outflowNode = summaryNodes['op_outflows'];
    if (!ecNode?.values || periods.length === 0) return null;

    let peakVal = -Infinity, peakP = null;
    let troughVal = Infinity, troughP = null;
    periods.forEach(p => {
      const v = ecNode.values[p];
      if (v == null) return;
      if (v > peakVal)   { peakVal = v;   peakP = p; }
      if (v < troughVal) { troughVal = v; troughP = p; }
    });

    const endingPeriod = forecastPeriods[forecastPeriods.length - 1] || periods[periods.length - 1];
    const endingCash = endingPeriod ? (ecNode.values[endingPeriod] ?? null) : null;

    const forecastStartPeriod = forecastPeriods[0] || null;
    const forecastStartCash = forecastStartPeriod
      ? (bcNode?.values?.[forecastStartPeriod] ?? ecNode.values[forecastStartPeriod] ?? null)
      : null;
    const forecastEndCash = forecastPeriods.length
      ? (ecNode.values[forecastPeriods[forecastPeriods.length - 1]] ?? null)
      : null;

    const avgOutflowPerPeriod = forecastPeriods.length
      ? forecastPeriods.reduce((sum, period) => {
        const value = outflowNode?.values?.[period];
        return sum + Math.abs(Math.min(Number(value) || 0, 0));
      }, 0) / forecastPeriods.length
      : null;

    const grossBurnMonthly = toMonthlyRate(avgOutflowPerPeriod, granularity);

    let runwayValue = '—';
    let runwayDate = 'insufficient data';

    if (forecastPeriods.length && forecastStartCash != null && forecastEndCash != null) {
      const slopePerPeriod = (forecastEndCash - forecastStartCash) / forecastPeriods.length;

      if (forecastStartCash <= 0) {
        runwayValue = 'Now';
        runwayDate = 'cash at or below zero';
      } else if (slopePerPeriod >= 0) {
        runwayValue = 'CASH POS';
        runwayDate = 'Profitable';
      } else {
        const periodsToZero = forecastStartCash / Math.abs(slopePerPeriod);
        const monthsToZero = periodsToMonths(periodsToZero, granularity);

        if (periodsToZero <= forecastPeriods.length) {
          const zeroPeriodIndex = Math.min(
            forecastPeriods.length - 1,
            Math.max(0, Math.ceil(periodsToZero) - 1)
          );
          runwayValue = formatPeriodLabel(forecastPeriods[zeroPeriodIndex], granularity);
          runwayDate = 'projected zero date';
        } else {
          runwayValue = `${monthsToZero >= 10 ? monthsToZero.toFixed(0) : monthsToZero.toFixed(1)} mo`;
          runwayDate = 'in months';
        }
      }
    }

    return {
      peakVal:   peakVal  === -Infinity ? null : peakVal,
      peakP,
      troughVal: troughVal === Infinity  ? null : troughVal,
      troughP,
      endingCash,
      endingPeriod,
      grossBurnMonthly,
      runwayValue,
      runwayDate,
    };
  }, [summaryNodes, periods, forecastPeriods, granularity]);

  // ── Revenue bar chart data ────────────────────────────────────────────────
  const revenueChartData = useMemo(() => {
    return buildStackedBarChartData({
      components,
      componentId: 'revenue',
      actualPeriods,
      forecastPeriods,
      granularity,
      colors: CHART_COLORS,
      fadedColors: CHART_COLORS_FADED,
      stack: 'revenue',
    });
  }, [components, actualPeriods, forecastPeriods, granularity]);

  // ── Inflows bar chart data ─────────────────────────────────────────────────
  const inflowsChartData = useMemo(() => {
    return buildStackedBarChartData({
      components,
      componentId: 'op_inflows',
      actualPeriods,
      forecastPeriods,
      granularity,
      colors: CHART_COLORS,
      fadedColors: CHART_COLORS_FADED,
      stack: 'inflows',
    });
  }, [components, actualPeriods, forecastPeriods, granularity]);

  // ── Top outflows stacked bar data ──────────────────────────────────────────
  const outflowsChartData = useMemo(() => {
    return buildStackedBarChartData({
      components,
      componentId: 'op_outflows',
      actualPeriods,
      forecastPeriods,
      granularity,
      colors: CHART_COLORS,
      fadedColors: CHART_COLORS_FADED,
      stack: 'outflows',
      getValue: (v) => Math.abs(Math.min(Number(v) || 0, 0)),
    });
  }, [components, actualPeriods, forecastPeriods, granularity]);

  const hasData   = !!(forecast?.calendar && periods.length > 0);
  const granLabel = granularity === 'week' ? 'wks' : granularity === 'month' ? 'mos' : 'days';
  const actLabel  = actWin.length  ? `Last ${actWin.length}`  : 'Actuals';
  const fcstLabel = fcstWin.length ? `Next ${fcstWin.length}` : 'Forecast';

  return (
    <div className="wrapper ov-wrapper">
      <div className="ov-content">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="ov-header">
          <div className="page-title-group">
            <h2 className="page-title">Forecast Overview</h2>
            {forecast?.name && <p className="page-subtitle">{forecast.name}</p>}
          </div>
          {/* <button className="ov-back-btn" onClick={() => navigate('/reports')}>
            ← Reports
          </button> */}
        </div>

        {!hasData ? (
          <div className="ov-empty">
            <p>No forecast data available. Build a forecast first in the Forecast tab.</p>
          </div>
        ) : (
          <div className="ov-grid">

            {/* ── Top Left: Cash Flow Summary + Key Metrics ──────────── */}
            <div className="ov-card">
              <div className="ov-card-header">
                <span className="ov-card-title">Cash Flow Summary</span>
                <span className="ov-card-sub">
                  {actLabel} {granLabel} actuals · {fcstLabel} {granLabel} forecast
                </span>
              </div>
              <div className="ov-summary-table">
                <div className="ov-st-head">
                  <div className="ov-st-lbl-col" />
                  <div className="ov-st-num-col">Actuals</div>
                  <div className="ov-st-num-col">Forecast</div>
                  <div className="ov-st-num-col">Var $</div>
                  <div className="ov-st-num-col">Var %</div>
                </div>
                {tableRows.map(row => (
                  <div key={row.id} className={`ov-st-row${row.emphasized ? ' ov-st-row--em' : ''}`}>
                    <div className="ov-st-lbl-col">{row.label}</div>
                    <div className="ov-st-num-col ov-col--actual">{fmt(row.actVal)}</div>
                    <div className="ov-st-num-col ov-col--fcst">{fmt(row.fcstVal)}</div>
                    <div className={`ov-st-num-col ov-col--var ${row.id === 'beginning_cash' ? '' : (row.varD > 0 ? 'pos' : row.varD < 0 ? 'neg' : '')}`}>
                      {row.id === 'beginning_cash' ? '—' : (row.varD !== null ? fmt(row.varD) : '—')}
                    </div>
                    <div className={`ov-st-num-col ov-col--pct ${row.id === 'beginning_cash' ? '' : (row.varP > 0 ? 'pos' : row.varP < 0 ? 'neg' : '')}`}>
                      {row.id === 'beginning_cash' ? '—' : fmtPct(row.varP)}
                    </div>
                  </div>
                ))}
              </div>
              {metrics && (
                <div className="ov-metrics-strip">
                  <div className="ov-metric-mini">
                    <span className="ov-metric-mini-label">Highest Cash</span>
                    <span className="ov-metric-mini-value">{fmtCompact1(metrics.peakVal)}</span>
                    {metrics.peakP && <span className="ov-metric-mini-date">{formatPeriodLabel(metrics.peakP, granularity)}</span>}
                  </div>
                  <div className="ov-metric-mini">
                    <span className="ov-metric-mini-label">Lowest Cash</span>
                    <span className="ov-metric-mini-value">{fmtCompact1(metrics.troughVal)}</span>
                    {metrics.troughP && <span className="ov-metric-mini-date">{formatPeriodLabel(metrics.troughP, granularity)}</span>}
                  </div>
                  <div className="ov-metric-mini">
                    <span className="ov-metric-mini-label">Ending Cash</span>
                    <span className="ov-metric-mini-value">{fmtCompact1(metrics.endingCash)}</span>
                    {metrics.endingPeriod && <span className="ov-metric-mini-date">{formatPeriodLabel(metrics.endingPeriod, granularity)}</span>}
                  </div>
                  <div className="ov-metric-mini">
                    <div className="ov-metric-container">
                      <span className="ov-metric-mini-label">Runway</span>
                      <div className="ov-info-wrap">
                        <InfoIcon className="info-icon" />
                        <div className="ov-info-tooltip">
                          How many months of cash remain at the current burn rate, based on the linear trend from forecast start to end.
                        </div>
                      </div>
                    </div>
                    <span className="ov-metric-mini-value ov-metric-mini-value--runway">{metrics.runwayValue}</span>
                    <span className="ov-metric-mini-date">{metrics.runwayDate}</span>
                  </div>
                  <div className="ov-metric-mini">
                    <div className="ov-metric-container">
                      <span className="ov-metric-mini-label">Burn / Mo</span>
                      <div className="ov-info-wrap ov-info-wrap--right">
                        <InfoIcon className="info-icon" />
                        <div className="ov-info-tooltip">
                          Average monthly cash outflows over the forecast period. Excludes inflows — reflects gross spend, not net burn.
                        </div>
                      </div>
                    </div>
                    <span className="ov-metric-mini-value">{fmtCompact1(metrics.grossBurnMonthly)}</span>
                    <span className="ov-metric-mini-date">avg outflows</span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Top Right: Revenue Breakdown (stacked bar) ─────────── */}
            <div className="ov-card">
              <div className="ov-card-header">
                <span className="ov-card-title">Revenue Breakdown</span>
                <span className="ov-card-sub">Solid = actuals · Faded = forecast</span>
              </div>
              {revenueChartData ? (
                <div className="ov-rev-section">
                  <div className="ov-rev-chart-wrap">
                    <Bar data={revenueChartData} options={REV_CHART_OPTIONS} />
                  </div>
                  <div className="ov-rev-legend">
                    {revenueChartData.segLabels.map((label, i) => (
                      <span key={i} className="ov-rev-legend-item">
                        <span
                          className="ov-pie-dot"
                          style={{ background: CHART_COLORS[Math.min(i, CHART_COLORS.length - 1)] }}
                        />
                        <span className="ov-rev-legend-label">{label}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="ov-no-data">No revenue data available</div>
              )}
            </div>

            {/* ── Bottom Left: Inflows (stacked bar) ─────────────────── */}
            <div className="ov-card">
              <div className="ov-card-header">
                <span className="ov-card-title">Top Inflows</span>
                <span className="ov-card-sub">Solid = actuals · Faded = forecast</span>
              </div>
              {inflowsChartData ? (
                <div className="ov-rev-section">
                  <div className="ov-rev-chart-wrap">
                    <Bar data={inflowsChartData} options={REV_CHART_OPTIONS} />
                  </div>
                  <div className="ov-rev-legend">
                    {inflowsChartData.segLabels.map((label, i) => (
                      <span key={i} className="ov-rev-legend-item">
                        <span
                          className="ov-pie-dot"
                          style={{ background: CHART_COLORS[Math.min(i, CHART_COLORS.length - 1)] }}
                        />
                        <span className="ov-rev-legend-label">{label}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="ov-no-data">
                  {components?.length > 0
                    ? 'No inflow line items found in forecast structure.'
                    : 'No forecast data loaded.'}
                </div>
              )}
            </div>

            {/* ── Bottom Right: Top Outflows (stacked bar) ────────────── */}
            <div className="ov-card">
              <div className="ov-card-header">
                <span className="ov-card-title">Top Outflows</span>
                <span className="ov-card-sub">Solid = actuals · Faded = forecast</span>
              </div>
              {outflowsChartData ? (
                <div className="ov-rev-section">
                  <div className="ov-rev-chart-wrap">
                    <Bar data={outflowsChartData} options={REV_CHART_OPTIONS} />
                  </div>
                  <div className="ov-rev-legend">
                    {outflowsChartData.segLabels.map((label, i) => (
                      <span key={i} className="ov-rev-legend-item">
                        <span
                          className="ov-pie-dot"
                          style={{ background: CHART_COLORS[Math.min(i, CHART_COLORS.length - 1)] }}
                        />
                        <span className="ov-rev-legend-label">{label}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="ov-no-data">
                  {components?.length > 0
                    ? 'No outflow line items found in forecast structure.'
                    : 'No forecast data loaded.'}
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

export default Overview;
